import '../styles/raqtaq.css';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { UserProvider } from "../context/UserContext";
import { AudioPlayerProvider } from "../context/AudioPlayerContext";
import { VideoPlayerProvider } from "../context/VideoPlayerContext";
import { SocketProvider } from "../context/SocketContext";
import { TheaterModeProvider, useTheaterMode } from '../context/TheaterModeContext';
import TheaterModeToggle from '../components/TheaterModeToggle';
import { NavTop, NavPlay } from '../components/nav';
import ErrorBoundary from '../components/ErrorBoundary';
import Head from 'next/head';
import { StatusBar } from '@capacitor/status-bar';
import { SafeArea } from '@capacitor-community/safe-area';
import { Capacitor } from '@capacitor/core';
import RaqtaqLoading from '../components/RaqtaqLoading';
import { SunulifeContentProvider } from '../context/sunulifeContentContext';

function Sunulife({ Component, pageProps }) {
  const router = useRouter();
  const { asPath } = router;
  const [loading, setLoading] = useState(false);

  const videoRoutes = [
    '/en/videos/',
    '/fr/videos/',
    '/en/tv/',
    '/fr/tv/',
    '/en/music/',
    '/fr/music/',
    '/en/moments/',
    '/fr/moments/',
  ];

  const isVideosPage = videoRoutes.some(route => asPath.startsWith(route));

  const aiRoutes = [
    '/en/ai',
    '/fr/ia',
  ];

  const isAI = aiRoutes.some(route => asPath.startsWith(route));

  useEffect(() => {
    const handleRouteChangeStart = () => setLoading(true);
    const handleRouteChangeComplete = () => setLoading(false);

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeComplete);
    };
  }, [router.events]);

  useEffect(() => {
    fetch('/api/socket').catch(err => console.error('Socket.IO initialization error:', err));

    if ("serviceWorker" in navigator) {
      const onLoad = () => {
        navigator.serviceWorker
          .register("/sw.js")
          .catch(error => console.error("Service Worker registration failed:", error));
      };

      window.addEventListener("load", onLoad);
      return () => window.removeEventListener("load", onLoad);
    }

    if (Capacitor.isNativePlatform()) {
      StatusBar.setOverlaysWebView({ overlay: false }).catch(console.error);
    }

    if (Capacitor.getPlatform() === 'ios') {
      SafeArea.enable({
        config: {
          customColorsForSystemBars: true,
          statusBarColor: '#00000000',
          statusBarContent: 'light',
          navigationBarColor: '#00000000',
          navigationBarContent: 'light',
        },
      }).catch(console.error);
    }
  }, []);

  const { metadata, contentImage } = pageProps;
  const siteIsFR = asPath.startsWith('/fr');
  const melodieFR = asPath.startsWith('/fr/music/raqtaq');
  const melodieEN = asPath.startsWith('/en/music/raqtaq');

  return (
    <>
      <Head>
        <meta name="google" content="notranslate" />
        { isAI && (<>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="theme-color" content="#000000" />
        <meta property="og:site_name" content="raQtaQ" />
        <meta property="og:image" content={process.env.PIX + "/raqtaq-ai-chat.png"} />
        <link rel="icon" href="/favicon.ico" />
        <title>{siteIsFR ? `Sunulife IA : recherchez ou demandez tout à Sunulife.` : `Sunulife AI : Search or Ask Sunulife Anything.`}</title>
        <meta name="description" content={siteIsFR ? `Recherchez ou demandez tout à l'assistante alimentée par l'intelligence artificielle de Sunulife.` : `Search or ask anything, Sunulife’s artificial intelligence-powered assistant is here to help you.`} />
        <link rel="canonical" href={siteIsFR ? `https://Sunulife.com/fr/ia` : `https://Sunulife.com/en/ai`} />
        <meta property="og:title" content={siteIsFR ? `Sunulife IA : recherchez ou demandez tout à Sunulife.` : `Sunulife AI : Search or Ask Sunulife Anything.`} />
        <meta property="og:description" content={siteIsFR ? `Recherchez ou demandez tout à l'assistante alimentée par l'intelligence artificielle de Sunulife.` : `Search or ask anything, Sunulife’s artificial intelligence-powered assistant is here to help you.`} />
        <meta property="og:url" content={siteIsFR ? `https://Sunulife.com/fr/ia` : `https://Sunulife.com/en/ai`} />
        <meta property="og:image" content={process.env.PIX + "/raqtaq-ai-chat.png"} />
        <meta property="og:image:width" content="1024px" />
        <meta property="og:image:height" content="1024px" />
        </>)}
        { melodieFR || melodieEN ? (<>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="theme-color" content="#000000" />
        <meta property="og:site_name" content="raQtaQ" />
        <meta property="og:image" content={melodieEN ? (process.env.PIX + "/_raqtaq_melodies_africa.webp") : (process.env.PIX + "/_raqtaq_melodies_senegal.webp")} />
        <link rel="icon" href="/favicon.ico" />
        <title>{melodieEN ? `Melodies: The Best of African Music.` : `Mélodies : le meilleur de la musique sénégalaise.`}</title>
        <meta name="description" content={melodieEN ? `Dive into the vibrant world of African music with Sunulife. Discover Melodies, your ultimate destination for experiencing the best of African music. From traditional rhythms to modern sounds, explore a playlist that celebrates the richness and diversity of Africa's musical heritage. Feel the pulse of Afrobeat, highlife, mbalax, and more with Sunulife.` : `Plongez dans l’univers vibrant de la musique sénégalaise avec Sunulife. Découvrez Mélodies, votre destination incontournable pour savourer le meilleur de la musique sénégalaise. Des rythmes traditionnels aux sons modernes, plongez dans une playlist qui célèbre la richesse et la diversité de la culture musicale du Sénégal. Vibrez`} />
        <link rel="canonical" href={melodieEN ? `https://Sunulife.com/en/music/raqtaq` : `https://Sunulife.com/fr/music/raqtaq`} />
        <meta property="og:title" content={melodieEN ? `Melodies: The Best of African Music.` : `Mélodies : le meilleur de la musique sénégalaise.`} />
        <meta property="og:description" content={melodieEN ? `Dive into the vibrant world of African music with Sunulife. Discover Melodies, your ultimate destination for experiencing the best of African music. From traditional rhythms to modern sounds, explore a playlist that celebrates the richness and diversity of Africa's musical heritage. Feel the pulse of Afrobeat, highlife, mbalax, and more with Sunulife.` : `Plongez dans l’univers vibrant de la musique sénégalaise avec Sunulife. Découvrez Mélodies, votre destination incontournable pour savourer le meilleur de la musique sénégalaise. Des rythmes traditionnels aux sons modernes, plongez dans une playlist qui célèbre la richesse et la diversité de la culture musicale du Sénégal. Vibrez`} />
        <meta property="og:url" content={melodieEN ? `https://Sunulife.com/en/music/raqtaq` : `https://Sunulife.com/fr/music/raqtaq`} />
        <meta property="og:image" content={melodieEN ? (process.env.PIX + "/_raqtaq_melodies_africa.webp") : (process.env.PIX + "/_raqtaq_melodies_senegal.webp")} />
        <meta property="og:image:width" content="1024px" />
        <meta property="og:image:height" content="1024px" />
        </>) : (<>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <meta name="theme-color" content="#000000" />
        <meta property="og:site_name" content="raQtaQ" />
        <meta property="og:image" content={contentImage || "/icon-1024x1024.png"} />
        <link rel="icon" href="/favicon.ico" />
        <title>{metadata?.title || "Sunulife: Our African Journey. From Senegal to the World."}</title>
        <meta name="description" content={metadata?.description || "At Sunulife, we celebrate the vibrant tapestry of African and diaspora culture, where heritage fuels ambition. Dive into Society for thought-provoking articles on identity and history, sway to Music with Afrobeat and mbalax rhythms, capture Moments of cultural pride, explore Chronicles of migration and legacy, and gain Insights into traditions like Senegalese griots. Inspired by the resilience of the diaspora, we honor the past while soaring toward the future. Join us to connect, learn, and rise."} />
        {metadata?.openGraph && (
          <>
            <link rel="canonical" href={ (siteIsFR ? metadata.openGraph.url.replace('/en', '/fr') : metadata.openGraph.url) || (siteIsFR ? `https://Sunulife.com/fr/` : `https://Sunulife.com/en/`)} />
            <meta property="og:title" content={metadata.openGraph.title || (siteIsFR ? `Sunulife : Du Sénégal au monde. Notre culture, notre histoire.` : `Sunulife: Our African Journey. From Senegal to the World.`)} />
            <meta property="og:description" content={metadata.openGraph.description || (siteIsFR ? `Chez Sunulife, nous célébrons la richesse de la culture africaine et diasporique, où l’héritage nourrit l’ambition. Plongez dans Société pour des articles percutants sur l’identité et l’histoire, vibrez avec Musique sur des rythmes Afrobeat et mbalax, capturez des Moments de fierté culturelle, explorez des Chroniques sur la migration et l’héritage, et découvrez des Perspectives sur des traditions comme les griots sénégalais. Inspirés par la résilience de la diaspora, nous honorons le passé tout en visant l’avenir. Rejoignez-nous pour vous connecter, apprendre et grandir.` : `At Sunulife, we celebrate the vibrant tapestry of African and diaspora culture, where heritage fuels ambition. Dive into Society for thought-provoking articles on identity and history, sway to Music with Afrobeat and mbalax rhythms, capture Moments of cultural pride, explore Chronicles of migration and legacy, and gain Insights into traditions like Senegalese griots. Inspired by the resilience of the diaspora, we honor the past while soaring toward the future. Join us to connect, learn, and rise.`)} />
            <meta property="og:url" content={(siteIsFR ? metadata.openGraph.url.replace('/en', '/fr') : metadata.openGraph.url) || (siteIsFR ? `https://Sunulife.com/fr/` : `https://Sunulife.com/en/`)} />
            {metadata.openGraph.images?.map((image, index) => (
              <meta key={index} property="og:image" content={image.url} />
            ))}
            {metadata.openGraph.images?.map((image, index) => (
              <meta key={`og:image:alt:${index}`} property="og:image:alt" content={image.alt || (siteIsFR ? `Sunulife : Du Sénégal au monde. Notre culture, notre histoire.` : `Sunulife: Our African Journey. From Senegal to the World.`)} />
            ))}
            {metadata.openGraph.images?.map((image, index) => (
              image.width && <meta key={`og:image:width:${index}`} property="og:image:width" content={image.width.toString()} />
            ))}
            {metadata.openGraph.images?.map((image, index) => (
              image.height && <meta key={`og:image:height:${index}`} property="og:image:height" content={image.height.toString()} />
            ))}
            {metadata.openGraph.type && <meta property="og:type" content={metadata.openGraph.type || "website"} />}
            {metadata.openGraph.article && (
              <>
                <meta property="article:published_time" content={metadata.openGraph.article.published_time} />
                <meta property="article:modified_time" content={metadata.openGraph.article.modified_time} />
                <meta property="article:author" content={metadata.openGraph.article.author} />
                <meta property="article:section" content={metadata.openGraph.article.section} />
                {metadata.openGraph.article.tags?.map((tag, index) => (
                  <meta key={index} property="article:tag" content={tag} />
                ))}
              </>
            )}
          </>
        )}
        {metadata?.twitter && (
          <>
            <meta name="twitter:card" content={metadata.twitter.cardType || "summary_large_image"} />
            <meta name="twitter:site" content={metadata.twitter.site || "@raqtaqhub"} />
            <meta name="twitter:title" content={metadata.twitter.title || (siteIsFR ? `Sunulife : Du Sénégal au monde. Notre culture, notre histoire.` : `Sunulife: Our African Journey. From Senegal to the World.`)} />
            <meta name="twitter:description" content={metadata.twitter.description || (siteIsFR ? `Chez Sunulife, nous célébrons la richesse de la culture africaine et diasporique, où l’héritage nourrit l’ambition. Plongez dans Société pour des articles percutants sur l’identité et l’histoire, vibrez avec Musique sur des rythmes Afrobeat et mbalax, capturez des Moments de fierté culturelle, explorez des Chroniques sur la migration et l’héritage, et découvrez des Perspectives sur des traditions comme les griots sénégalais. Inspirés par la résilience de la diaspora, nous honorons le passé tout en visant l’avenir. Rejoignez-nous pour vous connecter, apprendre et grandir.` : `At Sunulife, we celebrate the vibrant tapestry of African and diaspora culture, where heritage fuels ambition. Dive into Society for thought-provoking articles on identity and history, sway to Music with Afrobeat and mbalax rhythms, capture Moments of cultural pride, explore Chronicles of migration and legacy, and gain Insights into traditions like Senegalese griots. Inspired by the resilience of the diaspora, we honor the past while soaring toward the future. Join us to connect, learn, and rise.`)} />
            <meta name="twitter:image" content={metadata.twitter.image || "/icon-1024x1024.png"} />
            <meta name="twitter:creator" content={metadata.twitter.creator || "@raqtaqhub"} />
          </>
        )}
        {metadata?.linkTags?.map((link, index) => (
          <link key={index} rel={link.rel} href={siteIsFR ? link.href.replace('/en', '/fr') : link.href} hreflang={link.hreflang} />
        ))}
        {metadata?.additionalMetaTags?.map((meta, index) => (
          <meta key={index} name={meta.name} content={meta.content} httpEquiv={meta.httpEquiv} />
        ))}
        {metadata?.schemaMarkup && (
          <script type="application/ld+json">
            {JSON.stringify(metadata.schemaMarkup)}
          </script>
        )}
      </>)}
      </Head>
      <TheaterModeProvider>
        <AppContent
          Component={Component}
          pageProps={{ ...pageProps, contentImage }} // Pass contentImage to AppContent
          isVideosPage={isVideosPage}
          loading={loading}
        />
      </TheaterModeProvider>
    </>
  );
}

function AppContent({ Component, pageProps, isVideosPage, loading }) {
  const { initialized } = useTheaterMode();

  if (!initialized) return <RaqtaqLoading />;

  return (
    <div className={isVideosPage ? 'videosPage' : ''}>
      <SocketProvider>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <UserProvider>
          <SunulifeContentProvider>
            <TheaterModeToggle />
            <NavTop />
            {isVideosPage ? (
              <VideoPlayerProvider>
                {loading && <RaqtaqLoading />}
                <Component {...pageProps} />
                <NavPlay />
              </VideoPlayerProvider>
            ) : (
              <AudioPlayerProvider>
                {loading && <RaqtaqLoading />}
                <Component {...pageProps} />
                <NavPlay />
              </AudioPlayerProvider>
            )}
            </SunulifeContentProvider>
          </UserProvider>
        </ErrorBoundary>
      </SocketProvider>
    </div>
  );
}

function ErrorFallback() {
  return (
    <div className="error-fallback">
      <h1>Something went wrong.</h1>
      <p>Please refresh the page or return to the <a href="/fr/">raQtaQ</a>.</p>
    </div>
  );
}

export default Sunulife;